import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeft, Save, User, Building2, Trash2,
    Briefcase, MapPin, Mail, Phone, Loader2,
    FileText, Camera, Download, X, UserCheck
} from 'lucide-react';
import { ClientService, PraticaService } from '../services/apiService';
import { useToast } from "../components/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from '../types';
import { getClientFullLabel } from '../utils/clientDisplay';
import { ColoreCalendarioField } from '../components/clienti/ColoreCalendarioField';

export const DettaglioClienteView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { user } = useAuth();
    const isAdmin = user?.role === UserRole.ADMIN;

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [mandati, setMandati] = useState<any[]>([]);
    const [loadingMandati, setLoadingMandati] = useState(false);

    // Stato per i nuovi file caricati in modalità Modifica
    const [newClientFiles, setNewClientFiles] = useState<File[]>([]);
    const [previewFile, setPreviewFile] = useState<{ url: string; name: string; isImage: boolean } | null>(null);

    const [formData, setFormData] = useState<any>({
        id: 0, nome: '', surname: '', email: '', telefono: '', tipo: 'Privato',
        // Per Azienda la ragione sociale sta in `ragioneSociale`, non in `nome`.
        ragioneSociale: '',
        indirizzo: '', citta: '', cap: '', provincia: '', codiceFiscale: '',
        partitaIva: '', luogoNascita: '', dataNascita: '',
        docTipo: 'Carta Identità', docNumero: '', docRilascioDa: '', docDataRilascio: '',
        pec: '', codiceSdi: '',
        // Dati del referente persona fisica (per cliente Azienda).
        indirizzoReferente: '', cittaReferente: '', capReferente: '', provinciaReferente: '',
        emailReferente: '', telefonoReferente: '',
        // Campi "per conto di terzi": per Azienda servono solo come fallback di
        // visualizzazione, si modificano solo per Privato con perContoDiTerzi=true.
        perContoDiTerzi: false,
        denominazioneTerzi: '', sedeTerzi: '', capTerzi: '', comuneTerzi: '', provinciaTerzi: '',
        pivaOrCfTerzi: '', codiceSdiTerzi: '', emailTerzi: '',
        sedeId: 0, praticheAttive: 0, praticheTotali: 0,
        files: [] // File restituiti dal backend
    });

    // Ultimi dati salvati: "Annulla" li rimette nel modulo scartando le modifiche.
    const savedDataRef = useRef<any>(null);

    useEffect(() => {
        const fetchClient = async () => {
            if (!id) return;
            setIsLoading(true);
            try {
                const res = await ClientService.getById(id);
                if (res.success && res.data) {
                    const c: any = res.data;
                    // Aziende salvate col vecchio formato: se la ragione sociale è in
                    // `denominazioneTerzi` o in `nome`, la mostriamo in `ragioneSociale`;
                    // al salvataggio finirà nel campo corretto.
                    if (c.tipo === 'Azienda' && !c.ragioneSociale) {
                        c.ragioneSociale = c.denominazioneTerzi || c.nome || '';
                        if (!c.partitaIva && c.pivaOrCfTerzi) c.partitaIva = c.pivaOrCfTerzi;
                        if (!c.codiceSdi && c.codiceSdiTerzi) c.codiceSdi = c.codiceSdiTerzi;
                        if (!c.pec && c.emailTerzi) c.pec = c.emailTerzi;
                    }
                    // Aziende create prima dei campi *Referente: email, telefono e residenza erano
                    // nei campi nativi. Li mostriamo come dati del soggetto finché non vengono separati.
                    if (c.tipo === 'Azienda') {
                        if (!c.emailReferente && c.email) c.emailReferente = c.email;
                        if (!c.telefonoReferente && c.telefono) c.telefonoReferente = c.telefono;
                        if (!c.indirizzoReferente && c.indirizzo) c.indirizzoReferente = c.indirizzo;
                        if (!c.cittaReferente && c.citta) c.cittaReferente = c.citta;
                        if (!c.capReferente && c.cap) c.capReferente = c.cap;
                        if (!c.provinciaReferente && c.provincia) c.provinciaReferente = c.provincia;
                    }
                    savedDataRef.current = c;
                    setFormData(c);
                } else {
                    addToast("Cliente non trovato", "error");
                    navigate('/clienti');
                }
            } catch (error) {
                console.error(error);
                addToast("Errore nel caricamento del cliente", "error");
            } finally {
                setIsLoading(false);
            }
        };

        fetchClient();
    }, [id, navigate, addToast]);

    // Mandati del cliente (PraticaListDTO), già esclusi i cancellati dal backend.
    useEffect(() => {
        const fetchMandati = async () => {
            if (!id) return;
            setLoadingMandati(true);
            try {
                const res = await PraticaService.getByClient(Number(id));
                const list = (res as any)?.data ?? (Array.isArray(res) ? res : []);
                setMandati(Array.isArray(list) ? list : []);
            } catch (error) {
                console.error('Errore caricamento mandati cliente:', error);
                setMandati([]);
            } finally {
                setLoadingMandati(false);
            }
        };
        fetchMandati();
    }, [id]);

    // Arrivando con #mandati scrolla alla sezione, ma solo dopo il caricamento
    // dei mandati perché l'altezza cambia.
    useEffect(() => {
        if (isLoading || loadingMandati) return;
        if (window.location.hash === '#mandati') {
            const el = document.getElementById('mandati');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [isLoading, loadingMandati]);

    // Eliminazione singolo allegato
    const handleDeleteExistingFile = async (fileId: number, fileName: string) => {
        if (window.confirm(`Sei sicuro di voler eliminare il documento "${fileName}"?`)) {
            try {
                await ClientService.deleteFile(fileId);
                addToast('Documento eliminato con successo.', 'success');

                // Rimuove il file dalla UI senza ricaricare la pagina
                setFormData((prev: any) => ({
                    ...prev,
                    files: prev.files.filter((f: any) => f.id !== fileId)
                }));
            } catch (error) {
                console.error(error);
                addToast("Errore durante l'eliminazione del documento.", 'error');
            }
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if(!id) return;

        setIsSaving(true);
        try {
            // Con nuovi file serve una chiamata multipart, altrimenti basta l'update normale.
            if (newClientFiles.length > 0) {
                const payload = new FormData();
                payload.append("data", JSON.stringify(formData));
                newClientFiles.forEach(file => {
                    const renamedFile = new File([file], `DocID_${file.name}`, { type: file.type });
                    payload.append("files", renamedFile);
                });

                // Usa updateMultipart di apiService
                await ClientService.updateMultipart(id, payload);
            } else {
                await ClientService.update(id, formData);
            }

            addToast('Cliente aggiornato con successo!', 'success');
            setIsEditing(false);
            setNewClientFiles([]); // Resetta i file pendenti

            // Ricarica per vedere i dati freschi
            const res = await ClientService.getById(id);
            if (res.success && res.data) {
                savedDataRef.current = res.data;
                setFormData(res.data);
            }

        } catch (error) {
            console.error(error);
            addToast('Errore durante l\'aggiornamento.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if(!id) return;
        if (formData.praticheAttive && formData.praticheAttive > 0) {
            addToast(`Impossibile eliminare: Il cliente ha ${formData.praticheAttive} pratiche attive.`, 'error');
            return;
        }
        if (window.confirm('Sei sicuro di voler eliminare definitivamente questo cliente?')) {
            try {
                await ClientService.delete(id);
                addToast('Cliente eliminato correttamente.', 'success');
                navigate('/clienti');
            } catch (error: any) {
                console.error(error);
                // Il 409 del BE contiene un messaggio leggibile.
                const msg = error?.response?.data?.message || 'Errore durante l\'eliminazione.';
                addToast(msg, 'error');
            }
        }
    };

    if (isLoading) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={40}/></div>;

    return (
        <div className="p-6 max-w-4xl mx-auto space-y-6 pb-24">
            {/* Header */}
            <div className="flex items-center justify-between">
                <button onClick={() => navigate('/clienti')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors">
                    <ChevronLeft size={20}/> Torna alla lista
                </button>

                <div className="flex gap-3">
                    {!isEditing ? (
                        <>
                            {/* Solo il titolare può eliminare un cliente (ClientController.delete è ADMIN-only). */}
                            {isAdmin && (
                                <button onClick={handleDelete} className="px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg font-medium transition-colors flex items-center gap-2">
                                    <Trash2 size={18}/> Elimina
                                </button>
                            )}
                            <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors shadow-sm">
                                Modifica Dati
                            </button>
                        </>
                    ) : (
                        <button onClick={() => {
                            // I documenti eliminati lo sono già sul server: si tiene l'elenco attuale.
                            if (savedDataRef.current) setFormData((prev: any) => ({ ...savedDataRef.current, files: prev.files }));
                            setIsEditing(false);
                            setNewClientFiles([]);
                        }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors">
                            Annulla
                        </button>
                    )}
                </div>
            </div>

            {/* Intestazione cliente */}
            <div className="flex items-center gap-4 mb-2">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-sm ${formData.tipo === 'Azienda' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                    {formData.tipo === 'Azienda' ? <Building2 size={32}/> : <User size={32}/>}
                </div>
                <div>
                    {/*
                      Nome con fallback per Azienda (ragioneSociale → denominazioneTerzi → nome),
                      incluso il referente: "Mario Rossi (Azienda Srl)".
                    */}
                    <h1 className="text-3xl font-bold text-gray-900">{getClientFullLabel(formData)}</h1>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{formData.tipo}</span>
                        {/*formData.praticheAttive && formData.praticheAttive > 0 ? (
                            <span className="flex items-center gap-1 text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded border border-green-100"><Briefcase size={12}/> {formData.praticheAttive} Pratiche Attive</span>
                        ) : (<span className="text-gray-400">Nessuna pratica attiva</span>)*/}
                    </div>
                </div>
            </div>

            {/* Mandati del cliente. L'ancora "#mandati" è usata da ClientView. */}
            <div id="mandati" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 scroll-mt-6">
                <div className="flex items-center justify-between border-b pb-3 mb-4">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                        <Briefcase size={18}/> Mandati del Cliente
                        {!loadingMandati && (
                            <span className="text-xs font-normal text-gray-400 normal-case">({mandati.length})</span>
                        )}
                    </h3>
                </div>

                {loadingMandati ? (
                    <div className="flex justify-center py-6"><Loader2 className="animate-spin text-blue-600" size={24}/></div>
                ) : mandati.length === 0 ? (
                    <div className="text-sm text-gray-500 italic py-4 text-center">Nessun mandato registrato per questo cliente.</div>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {mandati.map((m: any) => {
                            const stato = (m.stato || '').trim();
                            const statoLow = stato.toLowerCase();
                            const isAttivo = stato && !['conclusa','chiusa','annullata','annullato','cestino','rifiutata','rifiutato','bozza'].includes(statoLow);
                            const badgeClass = !stato
                                ? 'bg-gray-100 text-gray-500 border-gray-200'
                                : isAttivo
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-200';
                            return (
                                <li key={m.id}
                                    onClick={() => navigate(`/pratiche/${m.id}`)}
                                    className="flex items-center justify-between py-3 px-2 hover:bg-blue-50/40 rounded-lg cursor-pointer transition-colors group">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900 truncate group-hover:text-blue-700">
                                                {m.name || `Mandato #${m.id}`}
                                            </span>
                                            {m.mandatoCode && (
                                                <span className="text-xs text-gray-400 font-mono">{m.mandatoCode}</span>
                                            )}
                                        </div>
                                        {m.createdAt && (
                                            <div className="text-xs text-gray-400 mt-0.5">
                                                Creato il {new Date(m.createdAt).toLocaleDateString('it-IT')}
                                            </div>
                                        )}
                                    </div>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded border ${badgeClass} shrink-0 ml-3`}>
                                        {stato || 'N/D'}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <form onSubmit={handleSave} className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-8 relative">
                    {!isEditing && <div className="absolute inset-0 bg-gray-50/20 z-10 cursor-default rounded-xl" />}

                    {/*
                      Anagrafica.
                      - Privato: "Dati Personali" (nome, cognome, CF).
                      - Azienda: solo "Dati Soggetto", cioè il referente persona fisica.
                      I dati societari si modificano nel blocco "Agisce per conto di".
                    */}
                    <div>
                        <h3 className="text-sm font-bold uppercase tracking-wider border-b pb-2 mb-4 flex items-center gap-2 text-blue-700 border-blue-100">
                            <User size={18}/> {formData.tipo === 'Azienda' ? 'Dati Soggetto' : 'Dati Personali'}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {formData.tipo === 'Privato' ? (
                                <>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Nome</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" value={formData.nome} onChange={e => setFormData({...formData, nome: e.target.value})}/></div>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Cognome</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" value={formData.surname || ''} onChange={e => setFormData({...formData, surname: e.target.value})}/></div>
                                    <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-500 mb-1">Codice Fiscale</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg uppercase font-mono disabled:bg-gray-50" value={formData.codiceFiscale || ''} onChange={e => setFormData({...formData, codiceFiscale: e.target.value})}/></div>
                                </>
                            ) : (
                                <>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Nome</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.nome || ''} onChange={e => setFormData({...formData, nome: e.target.value})}/></div>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Cognome</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.surname || ''} onChange={e => setFormData({...formData, surname: e.target.value})}/></div>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Codice Fiscale</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg uppercase font-mono disabled:bg-gray-50" value={formData.codiceFiscale || ''} onChange={e => setFormData({...formData, codiceFiscale: e.target.value})}/></div>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Luogo Nascita</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.luogoNascita || ''} onChange={e => setFormData({...formData, luogoNascita: e.target.value})}/></div>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Data Nascita</label><input disabled={!isEditing} type="date" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.dataNascita || ''} onChange={e => setFormData({...formData, dataNascita: e.target.value})}/></div>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Email</label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16}/><input disabled={!isEditing} type="email" className="w-full pl-10 p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.emailReferente || ''} onChange={e => setFormData({...formData, emailReferente: e.target.value})}/></div></div>
                                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Telefono</label><div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16}/><input disabled={!isEditing} type="tel" className="w-full pl-10 p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.telefonoReferente || ''} onChange={e => setFormData({...formData, telefonoReferente: e.target.value})}/></div></div>
                                    <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-500 mb-1">Indirizzo Residenza</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.indirizzoReferente || ''} onChange={e => setFormData({...formData, indirizzoReferente: e.target.value})}/></div>
                                    <div className="grid grid-cols-6 gap-4 md:col-span-2">
                                        <div className="col-span-3"><label className="block text-xs font-medium text-gray-500 mb-1">Città</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.cittaReferente || ''} onChange={e => setFormData({...formData, cittaReferente: e.target.value})}/></div>
                                        <div className="col-span-2"><label className="block text-xs font-medium text-gray-500 mb-1">CAP</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.capReferente || ''} onChange={e => setFormData({...formData, capReferente: e.target.value})}/></div>
                                        <div className="col-span-1"><label className="block text-xs font-medium text-gray-500 mb-1">Prov</label><input disabled={!isEditing} type="text" maxLength={2} className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50" value={formData.provinciaReferente || ''} onChange={e => setFormData({...formData, provinciaReferente: e.target.value})}/></div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/*
                      Recapiti e indirizzo, solo Privato: per l'Azienda sarebbero ambigui tra
                      recapiti aziendali e del referente.
                    */}
                    {formData.tipo === 'Privato' && (
                        <div>
                            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider border-b pb-2 mb-4 flex items-center gap-2"><MapPin size={18}/> Recapiti & Indirizzo</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><label className="block text-xs font-medium text-gray-500 mb-1">Email</label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16}/><input disabled={!isEditing} type="email" className="w-full pl-10 p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}/></div></div>
                                <div><label className="block text-xs font-medium text-gray-500 mb-1">Telefono</label><div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16}/><input disabled={!isEditing} type="tel" className="w-full pl-10 p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})}/></div></div>
                                <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-500 mb-1">Indirizzo</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.indirizzo || ''} onChange={e => setFormData({...formData, indirizzo: e.target.value})}/></div>
                                <div className="grid grid-cols-6 gap-4 md:col-span-2">
                                    <div className="col-span-3"><label className="block text-xs font-medium text-gray-500 mb-1">Città</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.citta || ''} onChange={e => setFormData({...formData, citta: e.target.value})}/></div>
                                    <div className="col-span-2"><label className="block text-xs font-medium text-gray-500 mb-1">CAP</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.cap || ''} onChange={e => setFormData({...formData, cap: e.target.value})}/></div>
                                    <div className="col-span-1"><label className="block text-xs font-medium text-gray-500 mb-1">Prov</label><input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50" value={formData.provincia || ''} onChange={e => setFormData({...formData, provincia: e.target.value})}/></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/*
                      Agisce per conto di, come in NuovoClienteView.
                      - Privato: Proprio / Di terzi; il sotto-form usa i campi *Terzi.
                      - Azienda: sempre "Di terzi"; il sotto-form usa i campi nativi
                      (ragioneSociale, sede legale, partitaIva, codiceSdi, pec).
                    */}
                    {(() => {
                        const isAzienda = formData.tipo === 'Azienda';
                        const diTerziChecked = isAzienda || !!formData.perContoDiTerzi;
                        const showSubForm = isAzienda || !!formData.perContoDiTerzi;
                        const radioDisabled = !isEditing || isAzienda;
                        const aziendaTitle = isAzienda ? "Per un cliente Azienda il referente agisce sempre per conto dell'azienda" : undefined;
                        return (
                            <div>
                                <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wider border-b border-amber-100 pb-2 mb-4 flex items-center gap-2">
                                    <UserCheck size={18}/> Agisce per conto di
                                </h3>
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <label
                                        className={`flex items-center gap-2 p-3 border rounded-lg ${
                                            isAzienda
                                                ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                                : !isEditing
                                                    ? 'cursor-default'
                                                    : 'cursor-pointer'
                                        } ${(!diTerziChecked) ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
                                        title={aziendaTitle}>
                                        <input type="radio" disabled={radioDisabled} className="text-blue-600"
                                            checked={!diTerziChecked}
                                            onChange={() => setFormData({...formData, perContoDiTerzi: false})}/>
                                        <span className="text-sm font-bold">Proprio</span>
                                    </label>
                                    <label
                                        className={`flex items-center gap-2 p-3 border rounded-lg ${!isEditing ? 'cursor-default' : 'cursor-pointer'} ${diTerziChecked ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}
                                        title={aziendaTitle}>
                                        <input type="radio" disabled={!isEditing} className="text-amber-600"
                                            checked={diTerziChecked}
                                            onChange={() => !isAzienda && setFormData({...formData, perContoDiTerzi: true})}/>
                                        <span className="text-sm font-bold">Di terzi</span>
                                    </label>
                                </div>

                                {showSubForm && (isAzienda ? (
                                    <div className="bg-amber-50/40 p-4 rounded-xl border border-amber-100 space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Denominazione</label>
                                            <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                value={formData.ragioneSociale || ''}
                                                onChange={e => setFormData({...formData, ragioneSociale: e.target.value})}/>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Sede / Residenza</label>
                                            <input disabled={!isEditing} type="text" placeholder="Via, Città..." className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                value={formData.indirizzo || ''}
                                                onChange={e => setFormData({...formData, indirizzo: e.target.value})}/>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">CAP</label>
                                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                    value={formData.cap || ''}
                                                    onChange={e => setFormData({...formData, cap: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Comune</label>
                                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                    value={formData.citta || ''}
                                                    onChange={e => setFormData({...formData, citta: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Prov.</label>
                                                <input disabled={!isEditing} type="text" maxLength={2} className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50"
                                                    value={formData.provincia || ''}
                                                    onChange={e => setFormData({...formData, provincia: e.target.value})}/>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">P.IVA / C.F.</label>
                                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50"
                                                    value={formData.partitaIva || ''}
                                                    onChange={e => setFormData({...formData, partitaIva: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Codice SDI</label>
                                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50"
                                                    value={formData.codiceSdi || ''}
                                                    onChange={e => setFormData({...formData, codiceSdi: e.target.value})}/>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Mail / PEC Soggetto Terzo</label>
                                            <input disabled={!isEditing} type="email" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                value={formData.pec || ''}
                                                onChange={e => setFormData({...formData, pec: e.target.value})}/>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-amber-50/40 p-4 rounded-xl border border-amber-100 space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Denominazione</label>
                                            <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                value={formData.denominazioneTerzi || ''}
                                                onChange={e => setFormData({...formData, denominazioneTerzi: e.target.value})}/>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Sede / Residenza</label>
                                            <input disabled={!isEditing} type="text" placeholder="Via, Città..." className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                value={formData.sedeTerzi || ''}
                                                onChange={e => setFormData({...formData, sedeTerzi: e.target.value})}/>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">CAP</label>
                                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                    value={formData.capTerzi || ''}
                                                    onChange={e => setFormData({...formData, capTerzi: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Comune</label>
                                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                    value={formData.comuneTerzi || ''}
                                                    onChange={e => setFormData({...formData, comuneTerzi: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Prov.</label>
                                                <input disabled={!isEditing} type="text" maxLength={2} className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50"
                                                    value={formData.provinciaTerzi || ''}
                                                    onChange={e => setFormData({...formData, provinciaTerzi: e.target.value})}/>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">P.IVA / C.F.</label>
                                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50"
                                                    value={formData.pivaOrCfTerzi || ''}
                                                    onChange={e => setFormData({...formData, pivaOrCfTerzi: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Codice SDI</label>
                                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50"
                                                    value={formData.codiceSdiTerzi || ''}
                                                    onChange={e => setFormData({...formData, codiceSdiTerzi: e.target.value})}/>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Mail / PEC Soggetto Terzo</label>
                                            <input disabled={!isEditing} type="email" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50"
                                                value={formData.emailTerzi || ''}
                                                onChange={e => setFormData({...formData, emailTerzi: e.target.value})}/>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}

                    <ColoreCalendarioField
                        value={formData.coloreCalendario}
                        onChange={v => setFormData({ ...formData, coloreCalendario: v })}
                        clienteId={id ? Number(id) : undefined}
                        disabled={!isEditing} />

                    {/* Documento d'identità */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider border-b pb-2 mb-4 flex items-center gap-2">
                            <FileText size={18}/> Documento d'Identità
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Tipo Documento</label>
                                <select disabled={!isEditing} className="w-full p-2.5 border rounded-lg disabled:bg-gray-50 bg-white" value={formData.docTipo || 'Carta Identità'} onChange={e => setFormData({...formData, docTipo: e.target.value})}>
                                    <option value="Carta Identità">Carta d'Identità</option>
                                    <option value="Patente">Patente di Guida</option>
                                    <option value="Passaporto">Passaporto</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Numero Documento</label>
                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg uppercase disabled:bg-gray-50" value={formData.docNumero || ''} onChange={e => setFormData({...formData, docNumero: e.target.value})}/>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Rilasciato Da</label>
                                <input disabled={!isEditing} type="text" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.docRilascioDa || ''} onChange={e => setFormData({...formData, docRilascioDa: e.target.value})}/>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Data Rilascio</label>
                                <input disabled={!isEditing} type="date" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.docDataRilascio || ''} onChange={e => setFormData({...formData, docDataRilascio: e.target.value})}/>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Data Scadenza (Opzionale)</label>
                                <input disabled={!isEditing} type="date" className="w-full p-2.5 border rounded-lg disabled:bg-gray-50" value={formData.docDataScadenza || ''} onChange={e => setFormData({...formData, docDataScadenza: e.target.value})}/>
                            </div>
                        </div>
                    </div>
                </div>

                {/* File allegati / foto */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider border-b pb-2 mb-4 flex items-center gap-2">
                        <Camera size={18}/> Foto e Allegati Identificativi
                    </h3>

                    {/* Elenco file esistenti restituiti dal backend */}
                    {formData.files && formData.files.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            {formData.files.map((file: any) => {
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes((file.extension || '').toLowerCase());
                                const url = file.urlCompressed || file.urlOriginal;
                                return (
                                    <div key={file.id} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                                        {/* Anteprima immagine inline */}
                                        {isImage && url && (
                                            <div className="cursor-pointer bg-black" onClick={() => setPreviewFile({ url, name: file.name, isImage: true })}>
                                                <img
                                                    src={url}
                                                    alt={file.name}
                                                    className="w-full max-h-52 object-contain hover:opacity-90 transition-opacity"
                                                    onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
                                                />
                                            </div>
                                        )}
                                        {/* Riga info + azioni */}
                                        <div className="flex items-center justify-between p-3 gap-2">
                                            <div
                                                className="flex items-center gap-2 min-w-0 cursor-pointer hover:text-blue-600 transition-colors"
                                                onClick={() => url && setPreviewFile({ url, name: file.name, isImage })}
                                            >
                                                {isImage ? <Camera className="text-emerald-500 shrink-0" size={16}/> : <FileText className="text-blue-500 shrink-0" size={16}/>}
                                                <span className="font-medium text-xs text-gray-700 truncate">{file.name}</span>
                                            </div>
                                            <div className="flex gap-1 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => window.open(url, '_blank')}
                                                    className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 p-1.5 rounded-lg transition-colors flex items-center gap-1 text-xs font-bold"
                                                >
                                                    <Download size={13}/>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteExistingFile(file.id, file.name)}
                                                    className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 p-1.5 rounded-lg transition-colors flex items-center gap-1 text-xs font-bold"
                                                >
                                                    <Trash2 size={13}/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-sm text-gray-500 italic mb-4">Nessun documento d'identità caricato per questo cliente.</div>
                    )}

                    {/* Caricamento nuove foto (solo in modifica) */}
                    {isEditing && (
                        <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                            <label className="block text-sm font-bold text-blue-900 mb-2">Aggiungi nuove foto (Fronte/Retro)</label>
                            <label className="flex flex-col items-center justify-center gap-2 bg-white border-2 border-dashed border-blue-300 hover:bg-blue-50 hover:border-blue-500 cursor-pointer p-6 rounded-xl transition-all">
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*,application/pdf"
                                    capture="environment" /* Apre la fotocamera su tablet/smartphone */
                                    multiple
                                    onChange={(e) => {
                                        if(e.target.files) setNewClientFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                                    }}
                                />
                                <Camera size={28} className="text-blue-500" />
                                <span className="text-sm font-bold text-gray-700 text-center">Scatta foto o carica file</span>
                            </label>

                            {/* File pronti per il caricamento */}
                            {newClientFiles.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {newClientFiles.map((f, i) => (
                                        <div key={i} className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-200 text-sm">
                                            <span className="truncate flex-1 px-2">{f.name}</span>
                                            <button type="button" onClick={() => setNewClientFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-red-500 p-1.5 hover:bg-red-50 rounded">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Azioni footer */}
                {isEditing && (
                    <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex justify-center z-20 shadow-lg animate-in slide-in-from-bottom-2">
                        <div className="w-full max-w-4xl flex justify-end gap-4">
                            <button type="submit" disabled={isSaving} className="bg-green-600 text-white px-8 py-2.5 rounded-lg flex items-center gap-2 hover:bg-green-700 font-bold shadow-lg shadow-green-200 transition-all disabled:opacity-70">
                                {isSaving ? <Loader2 className="animate-spin" size={20}/> : <><Save size={20}/> Salva Modifiche</>}
                            </button>
                        </div>
                    </div>
                )}
            </form>

            {/* Modal Anteprima File */}
            {previewFile && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setPreviewFile(null)}>
                    <div className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setPreviewFile(null)}
                            className="absolute -top-12 right-0 text-white hover:text-gray-300 bg-black/50 hover:bg-black/70 p-2 rounded-full transition-colors"
                        >
                            <X size={24} />
                        </button>
                        {previewFile.isImage ? (
                            <img src={previewFile.url} alt={previewFile.name} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
                        ) : (
                            <iframe src={previewFile.url} title={previewFile.name} className="w-full h-[85vh] rounded-lg shadow-2xl bg-white" />
                        )}
                        <p className="absolute -bottom-10 text-white text-sm font-medium truncate max-w-full">{previewFile.name}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DettaglioClienteView;